<script>
    import {_, number} from 'svelte-i18n';
    import {getContext} from 'svelte';
    import Modal from '../Modal.svelte';
    import ItemsListing from '../ItemsListing.svelte';
    import {storePacks} from '../../stores.js';
    import {resourceDetails} from '../../globals.js';
    import {milestonesFromEvent, premiumPriceLabel, findSeasonPassPack} from './utils.js';

    let { event, open = $bindable(false) } = $props();

    const itemGlobals = { resourceDetails };

    // Same purchase entry point every other section uses (Shop.svelte provides it).
    const { buyPack: buyPackFromContext } = getContext('buyPack') ?? {};

    let premiumMilestones = $derived(milestonesFromEvent(event));

    // The webshop-only $19.99 pack (id 1180) that carries the `seasonPass` perk,
    // delivered in get-packs like any hidden pack. Matched by id, perk as fallback.
    let seasonPassPack = $derived(findSeasonPassPack($storePacks, event));

    // Price shown is the pack's localized paymentHub price (what they'll be charged).
    let priceLabel = $derived(premiumPriceLabel(event, seasonPassPack));

    // Ownership is granted only by the backend completing the real purchase;
    // there is no client-side unlock. Disabled until the pack is in get-packs.
    let canUnlock = $derived(!!seasonPassPack);

    function unlock() {
        if (!canUnlock) return;

        // Open the real payment flow for the season pass pack, exactly like packs
        // in other sections (Shop → Checkout → /api/store/init-payment). Premium
        // ownership flips only after the backend processes that purchase.
        open = false;
        buyPackFromContext?.({
            detail: {
                storePack: seasonPassPack,
                packDetails: {
                    title: $_('WebBattlePassPremiumModalTitle', { default: 'Premium Season Pass' }),
                    description: '',
                    subTitle: '',
                },
            },
        });
    }
</script>

<Modal class="battle-pass-premium" bind:open>
    <div class="premium-content">
        <div class="title-band">
            <h2>{$_('WebBattlePassPremiumModalTitle', { default: 'Premium Season Pass' })}</h2>
        </div>
        <p>{$_('WebBattlePassPremiumModalText', { default: 'Unlock the premium reward on every milestone — including the ones you already reached!' })}</p>
        <div class="premium-rewards">
            {#each premiumMilestones as milestone (milestone.key)}
                <div class="premium-milestone">
                    <span class="threshold">{$number(milestone.pointsNeeded)}</span>
                    <ItemsListing globals={itemGlobals} goldAmount={0} items={milestone.seasonPass}
                                  storePack={{}} hasMask={false}/>
                </div>
            {/each}
        </div>
        {#if event.hasEventPass}
            <button type="button" class="button primary" disabled>
                {$_('WebBattlePassPremiumOwned', { default: 'Premium unlocked' })}
            </button>
        {:else}
            <button type="button" class="button primary orange unlock-button"
                    disabled={!canUnlock} onclick={unlock}>
                {$_('WebBattlePassUnlockPremium', { default: 'Unlock Premium' })} — {priceLabel}
            </button>
        {/if}
    </div>
</Modal>

<style lang="scss">
  @use './battlepass.scss' as bp;

  .premium-content {
    @include bp.bp-parchment-panel;
    padding: 0 18px 18px;
    width: min(92vw, 560px);
    text-align: center;
    color: var(--color-darkGrey);

    .title-band {
      margin: 0 -18px 10px;
      padding: 14px 18px 12px;
      background: linear-gradient(110deg, #c9a14e 0%, #f0d9a0 50%, #c9a14e 100%);
      border-radius: 9px 9px 0 0;

      h2 {
        margin: 0;
        font-family: var(--font-family-sans-condensed);
        font-weight: bold;
        font-size: 1.7em;
        color: #2a1004;
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
      }
    }

    p {
      margin: 0 0 10px;
      font-weight: bold;
      color: #5a2c0e;
    }

    .premium-rewards {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin: 12px 0;
      max-height: 48vh;
      overflow-y: auto;
      padding-right: 4px;
      scrollbar-width: thin;
      scrollbar-color: #8a6228 rgba(120, 70, 20, 0.2);

      .premium-milestone {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 4px 10px;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.45);
        border: 1px solid #d8a93c;

        .threshold {
          flex-shrink: 0;
          min-width: 60px;
          padding: 2px 8px;
          border-radius: 12px;
          background: radial-gradient(ellipse at 35% 30%, #5a2c0e 0%, #240c05 85%);
          border: 1px solid #d8a93c;
          font-family: var(--font-family-sans-condensed);
          font-size: 0.9em;
          font-weight: bold;
          color: rgb(255, 217, 87);
        }

        :global(.items),
        :global(.rewards) {
          background: none;
          margin: 0;
          flex: 1;
          min-width: 0;
        }

        :global(.item) {
          border-bottom: none;
        }
      }
    }

    .unlock-button {
      width: 100%;
      min-height: 46px;
      font-family: var(--font-family-sans-condensed);
      font-size: 19px;
      padding: 0.4rem 1.5rem;
    }
  }
</style>
